import type { SurfaceIntentV1 } from "@/bridge/omnicast/emitSurfaceIntentV1";

import type { TimelinePublishStripSignal } from "./TimelinePublishStrip";

function clamp01(value: unknown, fallback = 0): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return Math.max(0, Math.min(1, fallback));
  return Math.max(0, Math.min(1, n));
}

export function collectNpcThresholds(probs: Record<string, unknown> | null | undefined): string[] {
  if (!probs) return [];

  const thresholds: string[] = [];
  if (clamp01(probs.overload, 0) >= 0.75) thresholds.push("overload>0.75");
  if (clamp01(probs.safety, 1) <= 0.5) thresholds.push("safety<0.50");
  if (clamp01(probs.attention, 0) >= 0.7) thresholds.push("attention>0.70");
  if (clamp01(probs.emotion, 0) >= 0.7) thresholds.push("emotion>0.70");
  return thresholds;
}

export function buildPublishStripSignals(args: {
  npcRuntime: Record<string, unknown> | null;
  surfaceIntent: SurfaceIntentV1 | null;
  phaseRuntime: Record<string, unknown> | null;
  playheadSec: number;
}): TimelinePublishStripSignal[] {
  const signals: TimelinePublishStripSignal[] = [];

  if (args.npcRuntime) {
    const probs = args.npcRuntime.probs && typeof args.npcRuntime.probs === "object"
      ? (args.npcRuntime.probs as Record<string, unknown>)
      : null;
    const thresholdsCrossed = collectNpcThresholds(probs);
    signals.push({
      kind: "npc_runtime",
      source: "runtime",
      label: thresholdsCrossed.length > 0 ? `NPC runtime ${thresholdsCrossed.join(", ")}` : "NPC runtime steady",
      intensity01: Math.max(
        clamp01(probs?.attention, 0),
        clamp01(probs?.emotion, 0),
        clamp01(probs?.overload, 0),
        1 - clamp01(probs?.safety, 1),
      ),
      thresholdsCrossed,
      payload: {
        ...args.npcRuntime,
        timeSec: Number.isFinite(Number(args.npcRuntime.playheadSec)) ? Number(args.npcRuntime.playheadSec) : args.playheadSec,
      },
    });
  }

  if (args.surfaceIntent) {
    const thresholdsCrossed = [
      String(args.surfaceIntent.intent ?? "").trim() && `intent:${String(args.surfaceIntent.intent).trim()}`,
      String(args.surfaceIntent.suggestMode ?? "").trim() && `suggestMode:${String(args.surfaceIntent.suggestMode).trim()}`,
    ].filter(Boolean) as string[];

    signals.push({
      kind: "surface_intent",
      source: "runtime",
      label: `Surface ${String(args.surfaceIntent.intent ?? "ambient").trim() || "ambient"}`,
      intensity01: clamp01(args.surfaceIntent.intensity01, 0),
      thresholdsCrossed,
      payload: {
        ...args.surfaceIntent,
        playheadSec: args.playheadSec,
        timeSec: args.playheadSec,
      },
    });
  }

  if (args.phaseRuntime) {
    signals.push({
      kind: "phase",
      source: "runtime",
      label: `Phase ${String(args.phaseRuntime.phase ?? "setup")}`,
      intensity01: clamp01(args.phaseRuntime.intensity, 0),
      payload: {
        ...args.phaseRuntime,
        timeSec: Number.isFinite(Number(args.phaseRuntime.playheadSec)) ? Number(args.phaseRuntime.playheadSec) : args.playheadSec,
      },
    });
  }

  return signals;
}